import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { Store, Action } from '@ngrx/store';
import { FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { ActionBase } from '../../core/store/action/ActionBase';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { ModuleConfiguration } from '../messages.module.config';
import * as messagesActions from '../store/actions';
import { MessagesModel } from '../models/MessagesModel';
import { DateFramedMessageModel } from '../models/DateFramedMessageModel';
import * as fromStore from '../store/reducers';
import { FormArray } from '@angular/forms/src/model';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { MatTableDataSource } from '@angular/material';
import { CustomOption, QuillModules, QuillEditorComponent } from 'ngx-quill';
import { maxPlainTextLength } from "../../common/validators/maxPlainTextLengthValidator";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-messages-workspace',
  templateUrl: './messages-workspace.component.html',
  styleUrls: ['./messages-workspace.component.scss']
})
export class MessagesWorkspaceComponent implements OnInit, OnDestroy {
  messages: MessagesModel;
  key: string;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  formArrayInitMethods = new Map<string, () => void>();
  actionIdentifiers: string[] = [];
  disclaimerForm: FormGroup;
  activeNotificationsForm: FormGroup;
  broadcastForm: FormGroup;
  actionLoading: boolean;
  activeNotifications: DateFramedMessageModel[] = [];
  displayedColumns = ['message', 'expirationTimestamp'];
  cursorPosition: number = 0;
  dataSource = new MatTableDataSource<DateFramedMessageModel>(); 
  editorModules = {
    toolbar: false,
    keyboard:{
      bindings:{
        tab:{ key: 9, handler(){ return true; } }
      }
    }
  }
  currentDate: Date;
  maxDisclaimerLength = 1000;
  maxNotificationLength = 500;
  maxBroadcastLength = 150;
  disclaimerLength: number;

  viewMessages: string = 'ConfigurationModule.Messages:View';
  editMessages: string = 'ConfigurationModule.Messages:Edit';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  @ViewChild('editor')
  editor: QuillEditorComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef) { }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }

  ngOnDestroy() {
    this.storeDispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, MessagesModel));
    this.destroyed$.next(true);
  }

  refreshMessages() {
    this.store.dispatch(new messagesActions.GetMessagesAction());
  }

  ngOnInit() {
    this.refreshMessages();
    this.currentDate = new Date();
    this.store
      .select(fromStore.getMessages)
      .takeUntil(this.destroyed$)
      .subscribe((v: MessagesModel) => {
        if (v) {
          this.messages = v;
          this.key = v.key;
          if (!this.disclaimerForm) {
            this.initForms();
          }
          else {
            this.updateNotifications();
          }
          this.updateMessagesModel();
          this.changeDetectorRef.markForCheck();
        } else {
          this.refreshMessages();
        }
      });

    var updateTypes = [      
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, MessagesModel, 'draftDisclaimerDescription'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, MessagesModel, 'notification'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, MessagesModel, 'draftBroadcastMessage'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, MessagesModel, 'activeBroadcastMessage')
    ];

    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelSuccessAction<MessagesModel, any>) => a.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<MessagesModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .filter((a: workspaceActions.UpdateModelErrorAction<MessagesModel, any>) => a.payload.action.payload.key === this.key)
      .subscribe((a: workspaceActions.UpdateModelErrorAction<MessagesModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });

    this.actions$.ofType(messagesActions.ACTIVATE_DRAFT_DISCLAIMER_DESCRIPTION_SUCCESS_ACTION)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.actionLoading = false;
        this.alerts.addSuccess('Activate draft disclaimer description success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(messagesActions.ACTIVATE_DRAFT_BROADCAST_MESSAGE_SUCCESS_ACTION)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.actionLoading = false;
        this.alerts.addSuccess('Activate draft broadcast message success');
        this.broadcastForm.markAsUntouched();
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(messagesActions.EXPIRE_NOTIFICATION_SUCCESS_ACTION)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.actionLoading = false;
        this.alerts.addSuccess('Expire notification success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(messagesActions.EXPIRE_BROADCAST_SUCCESS_ACTION)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.actionLoading = false;
        this.alerts.addSuccess('Expire broadcast success');
        this.changeDetectorRef.markForCheck();
      });
  }

  updateNotifications() {
    var formArray = (this.activeNotificationsForm.controls['activeNotifications'] as FormArray);
    this.activeNotifications = this.messages.activeNotifications;
    var indexesToRemove = [];
    formArray.controls.forEach(control => {
      var notification = this.activeNotifications.find(
        n => n.key === control.value.key );
      if (!notification) {
        indexesToRemove.push(formArray.controls.indexOf(control));
      }
    });
    indexesToRemove.forEach(i => formArray.removeAt(i));
    this.dataSource.data = this.messages.historyNotifications;
  }

  updateMessagesModel() {
    if(this.messages){
      this.messages.activeBroadcastMessage = this.messages.activeBroadcastMessage || {
        $type: "",
        expirationTimestamp: undefined,
        key: undefined,
        value: undefined        
      };
      this.messages.draftBroadcastMessage = this.messages.draftBroadcastMessage  || {
        $type: "",
        expirationTimestamp: undefined,
        key: undefined,
        value: undefined        
      };
    }
    this.disclaimerForm.patchValue(this.messages);
    this.disclaimerForm.markAsPristine();
    this.broadcastForm.patchValue(this.messages);
    this.broadcastForm.markAsPristine();
    this.activeNotificationsForm.patchValue(this.messages);
    this.activeNotificationsForm.markAsPristine();
  }

  initForms(): void {
    this.disclaimerForm = this.formBuilder.group({
      key: [''],
      activeDisclaimerDescription: ['', Validators.required],
      draftDisclaimerDescription: ['', Validators.compose([Validators.required, maxPlainTextLength(this.maxDisclaimerLength)])],
    },
      null,
      this.destroyed$,
      (control) => {
        var model = control.value;
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, MessagesModel, 'draftDisclaimerDescription', { key: this.key, model: model, control: control }));
      },
      this.changeDetectorRef
    );

    this.dataSource.data = this.messages.historyNotifications;
    this.activeNotifications = this.messages.activeNotifications;

    this.activeNotificationsForm = this.formBuilder.group({
      activeNotifications: this.formBuilder.array(
        (this.activeNotifications || []).map(() => this.initNotification(true)))
    });

    this.formArrayInitMethods.set('activeNotifications', () => this.initNotification());

    this.broadcastForm = this.formBuilder.group({
      key: [''],
      draftBroadcastMessage: this.formBuilder.group({
        key: [''],
        value: ['', Validators.compose([Validators.required, Validators.maxLength(this.maxBroadcastLength)])],
        expirationTimestamp: [{ value: null }, Validators.required]
        },
        null,
        this.destroyed$,
        (control) => {
          this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, MessagesModel, 'draftBroadcastMessage', { key: this.key, model: control.value, control: control }));
        },
        this.changeDetectorRef),
      activeBroadcastMessage: this.formBuilder.group({
        key: [''],
        value: ['', Validators.required],
        expirationTimestamp: [{ value: null }, Validators.required]
      },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, MessagesModel, 'activeBroadcastMessage', { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef)
    });
  }

  initNotification(disable?: boolean) {
    return this.formBuilder.group({
        key : [''],
        value: ['', Validators.compose([Validators.required, Validators.maxLength(this.maxNotificationLength)])],
        expirationTimestamp: ['', Validators.required]
      },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, MessagesModel, 'notification', { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
  }

  activateDisclaimer() {
    this.actionLoading = true;
    this.storeDispatch(new messagesActions.ActivateDraftDisclaimerDescriptionAction());
  }

  activateBroadcast() {
    this.actionLoading = true;
    this.storeDispatch(new messagesActions.ActivateDraftBroadcastMessageAction());
  }

  addNotification(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
    this.currentDate = new Date();
  }

  expireNotification(form, notification: AutoSaveFormGroup, index) {
    this.actionLoading = true;
    notification.initializeRemove();
    this.storeDispatch(new messagesActions.ExpireNotificationAction({ notification: notification.value }));
  }

  expireBroadcast() {
    this.actionLoading = true;
    this.storeDispatch(new messagesActions.ExpireBroadcastAction());
  }

  back() {
    this.router.navigateByUrl('/administration');
  }

  onChange($event) {
    if ($event.source === 'user') {
      this.cursorPosition = this.editor.quillEditor.getSelection(false);
      this.editor.quillEditor.setSelection(this.cursorPosition, 0, 'user');
    }
    this.currentDate = new Date();
    var html = $event.html == null ? "" : $event.html;
    if ($event.html != null) {
      this.disclaimerLength = html.replace(/<\/?[^>]+(>|$)/g, "").trim().length;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/messages/components/messages-workspace.component.ts